"use client";

import { useState, useRef, useEffect } from "react";
import { FolderArchive } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import { useTranslations } from "next-intl";
import { PackFile } from "@/lib/packFiles";
import { useSession } from "next-auth/react";

interface ExportPublicFolderProps {
  buttonText?: string;
  buttonClassName?: string;
  files?: PackFile[];
  folderName?: string;
  locale?: string;
}

function inferLocaleFromFolderName(folderName?: string) {
  if (!folderName) return undefined;
  const match = folderName.match(/pack[_-]([a-z]{2})(?:[_-]|$)/i);
  return match?.[1]?.toLowerCase();
}

function inferLocaleFromFiles(files?: PackFile[]) {
  if (!files?.length) return undefined;
  const firstPath = files[0]?.path;
  if (!firstPath) return undefined;
  const match = firstPath.match(/\/packs\/([a-z]{2})(?:[-_][A-Z]{2})?\//i);
  return match?.[1]?.toLowerCase();
}

export default function ExportPublicFolder({
  buttonText,
  buttonClassName = "",
  files: customFiles,
  folderName,
  locale,
}: ExportPublicFolderProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const tr = useTranslations("Padel-Master-The-Game-rest");

  const resolvedLocale =
    locale ||
    inferLocaleFromFolderName(folderName) ||
    inferLocaleFromFiles(customFiles) ||
    "en";

  const packName = folderName ?? `pack_${resolvedLocale}`;
  const buttonLabel = buttonText ?? tr("export");

  const { data: session } = useSession();
  const userId = session?.user?.id;

  // ─── KEY FIX: lock button width before swapping label → percentage ────────
  // Without this, "Export" (text) vs "1%" vs "95%" vs "100%" are all
  // different widths, so the button visibly resizes/jiggles as the download
  // progresses. We measure the button's natural width once (on first render,
  // showing the label) and pin it via inline style for the whole download.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [lockedWidth, setLockedWidth] = useState<number | null>(null);

  useEffect(() => {
    // Capture the button's rendered width while it still shows the label,
    // before any download starts.
    if (buttonRef.current && lockedWidth === null) {
      setLockedWidth(buttonRef.current.offsetWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadAsZip = async () => {
    setIsDownloading(true);
    setDownloadProgress(1);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    try {
      const url = `/api/packs?locale=${encodeURIComponent(resolvedLocale)}&filename=${encodeURIComponent(packName)}`;
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const text = await response.text();
        let message = "Failed to download pack file.";
        try {
          const json = JSON.parse(text);
          if (json?.error) message = json.error;
          if (json?.missingFiles?.length) {
            console.error("Missing files on server:", json.missingFiles);
          }
        } catch {
          message = text || message;
        }
        throw new Error(message);
      }

      if (!response.body) {
        throw new Error("No response body received.");
      }

      // ── Real streaming progress ──────────────────────────────────────
      const contentLength = response.headers.get("Content-Length");
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      const reader = response.body.getReader();
      const chunks: Uint8Array<ArrayBuffer>[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;

        if (total > 0) {
          // Accurate progress: scale to 0–90 % while streaming
          setDownloadProgress(Math.round((received / total) * 90));
        } else {
          // No Content-Length header — advance slowly so UI feels alive
          setDownloadProgress((p) => Math.min(p + 3, 85));
        }
      }

      // Reassemble all chunks into a single Blob
      const blob = new Blob(chunks, { type: "application/zip" });
      setDownloadProgress(95);
      // ────────────────────────────────────────────────────────────────

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${packName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      setDownloadProgress(100);

      // Track download in backend (non-critical)
      if (userId) {
        try {
          await fetch(`${process.env.NEXT_PUBLIC_URL}infoid/${userId}`, {
            method: "PUT",
            headers: {
              Authorization: `Token ${process.env.NEXT_PUBLIC_TOKEN}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ is_create_order: true }),
          });
        } catch (trackingError) {
          console.warn("Failed to track download:", trackingError);
        }
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const message =
        err instanceof Error
          ? err.name === "AbortError"
            ? "Download timed out. Please try again."
            : err.message
          : "Unknown error occurred.";
      console.error("Error downloading pack zip:", err);
      setError(message);
    } finally {
      setIsDownloading(false);
      setTimeout(() => setDownloadProgress(0), 800);
    }
  };

  return (
    <div>
      <div className="animate-fade-slide-up-delay-1">
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            downloadAsZip();
          }}
          disabled={isDownloading}
          style={lockedWidth ? { width: lockedWidth } : undefined}
          className={
            `text-center text-secondary flex gap-3 font-medium text-xs md:text-sm lg:text-base ` +
            `hover:bg-a hover:text-white border-1 border-a rounded-xl px-4 w-80 lg:96 py-4 capitalize ` +
            `transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm ` +
            `hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring ` +
            `disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 ` +
            `justify-center ` +
            buttonClassName
          }
        >
          <FolderArchive size={22} />
          {isDownloading ? `${downloadProgress}%` : buttonLabel}
          <BsDownload size={18} />
        </button>
      </div>

      {isDownloading && (
        <div className="w-full bg-gray-700 rounded-full h-2 mb-4 mt-4 overflow-hidden">
          <div
            className="bg-b h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${downloadProgress}%` }}
          />
        </div>
      )}

      {error && (
        <p className="text-red-500 text-xs mt-2">
          ⚠ {error}
        </p>
      )}
    </div>
  );
}